import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Sidebar } from "@/components/Sidebar";
import { MobileNavigation } from "@/components/MobileNavigation";
import { Footer } from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUser } from "@/hooks/use-user";
import { useStore } from "@/hooks/use-store";
import { useStoreStats } from "@/hooks/use-store-stats";
import { StoreRegistration } from "@/components/store/StoreRegistration";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ImageUploader } from "@/components/store/ImageUploader";
import { ImageLibrary } from "@/components/store/ImageLibrary";
import LidoAdvancedCanvasEditor from "@/components/store/LidoAdvancedCanvasEditor";
import ProductsList from "@/components/store/ProductsList";
import { Store, Image, Palette, Package, BarChart3, Settings } from "lucide-react";
import { toast } from "sonner";

export default function MyStore() {
  const { user, isLoading: userLoading } = useUser();
  const { store, loading: storeLoading } = useStore();
  const { data: storeStats, isLoading: statsLoading } = useStoreStats(store?.id || '');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  if (!user && !userLoading) {
    navigate("/auth");
    return null;
  }

  if (userLoading || storeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Se não tem loja, mostrar cadastro
  if (!store) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <Sidebar />
        <main className={`${isMobile ? 'pt-16 pb-20' : 'sm:pl-16 pt-20 pl-4 pb-16'} transition-all duration-300`}>
          <StoreRegistration onSuccess={() => window.location.reload()} />
        </main>
        <Footer />
        {isMobile && <MobileNavigation />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Sidebar />
      
      <main className={`${isMobile ? 'pt-16 pb-20' : 'sm:pl-16 pt-20 pl-4 pb-16'} transition-all duration-300`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header da Loja */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold">{store.store_name}</h1>
                <p className="text-muted-foreground">Gerencie sua loja e suas melhores artes! </p>
              </div>
              <Button>
                <Settings className="h-4 w-4 mr-2" />
                Configurações
              </Button>
            </div>
          </div>

          {/* Tabs de Navegação */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 size={16} />
                Visão Geral
              </TabsTrigger>
              <TabsTrigger value="images" className="flex items-center gap-2">
                <Image size={16} />
                Minhas Imagens
              </TabsTrigger>
              <TabsTrigger value="editor" className="flex items-center gap-2">
                <Palette size={16} />
                Editor de Roupas
              </TabsTrigger>
              <TabsTrigger value="products" className="flex items-center gap-2">
                <Package size={16} />
                Produtos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total de Imagens
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {statsLoading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        storeStats?.total_images || 0
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Imagens na biblioteca
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Designs Criados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {statsLoading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        storeStats?.designs_created || 0
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Designs no total
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Produtos Ativos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {statsLoading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        storeStats?.active_products || 0
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      À venda agora
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Vendas do Mês
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {statsLoading ? (
                        <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                      ) : (
                        `R$ ${(storeStats?.monthly_sales || 0).toFixed(2)}`
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Faturamento mensal
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Bem-vindo à sua loja!</CardTitle>
                  <CardDescription>
                    Comece fazendo upload de suas imagens e criando seus primeiros designs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button onClick={() => setActiveTab("images")}>
                      <Image className="h-4 w-4 mr-2" />
                      Upload de Imagens
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab("editor")}>
                      <Palette className="h-4 w-4 mr-2" />
                      Criar Design
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="images" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Biblioteca de Imagens</h2>
                  <p className="text-muted-foreground">
                    Gerencie suas imagens e organize em pastas
                  </p>
                </div>
              </div>

              <ImageUploader storeId={store.id} />
              <ImageLibrary storeId={store.id} />
            </TabsContent>

            <TabsContent value="editor" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Editor de Designs Ink</h2>
                  <p className="text-muted-foreground">
                    Edite da melhor forma as suas roupas, repeito a autoria do desenho e siga o procedomento de estampas
                  </p>
                </div>
              </div>

              <LidoAdvancedCanvasEditor storeId={store.id} />
            </TabsContent>

            <TabsContent value="products" className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Produtos da Loja</h2>
                  <p className="text-muted-foreground">
                    Gerencie os produtos criados com seus designs personalizados
                  </p>
                </div>
              </div>

              <ProductsList storeId={store.id} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      <Footer />
      {isMobile && <MobileNavigation />}
    </div>
  );
}
